"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  exponentForm,
  factorize,
  MAX_FACTOR_INPUT,
} from "@/lib/tool-kit/math/primes";
import {
  MAX_DIGITS,
  MAX_RANGE_END,
  MAX_RANGE_WIDTH,
  type MillerRabinRound,
  nextPrime,
  type ParsedNumber,
  type PrimalityResult,
  parseCandidate,
  previousPrime,
  primesInRange,
  provenLimit,
  rangeStats,
  readRange,
  testPrimality,
} from "@/lib/tool-kit/number-theory/primality";

/** English strings. A translated content file overrides these through `ui`. */
export const PRIME_NUMBER_CHECKER_STRINGS = {
  number: "Number to test",
  numberHint:
    "A whole number up to 200 digits. Powers work too: 2^61-1 or 2^16+1. Spaces between digit groups are ignored.",
  results: "Results",
  verdict: "Verdict",
  prime: "Prime",
  probablePrime: "Probable prime",
  composite: "Composite",
  neither: "Neither prime nor composite",
  digits: "Digits",
  previousPrime: "Previous prime",
  nextPrime: "Next prime",
  noPrevious: "None",
  factorization: "Prime factorization",
  smallestFactor: "Smallest prime factor",
  copyResult: "Copy result",
  copied: "Copied!",
  summaryPrime: "{n} is prime.",
  summaryProbable: "{n} is a probable prime.",
  summaryComposite: "{n} is composite.",
  summaryNeither: "{n} is neither prime nor composite.",
  explainTrial: "{p} divides {n}, so it is not prime: {n} = {p} × {q}.",
  explainNeither:
    "A prime is a whole number greater than 1 with exactly two divisors. 0 and 1 do not qualify, and 1 is not composite either.",
  explainSmallPrime: "{n} is one of the 25 primes below 100.",
  provenNote:
    "This is a proof, not a probability. Miller–Rabin with the {count} {bases} below is deterministic for every number under {limit}.",
  baseOne: "one: base|other: bases",
  witnessNote:
    "A Miller–Rabin witness is a certificate: the base {base} shows that {n} is composite, so no luck is involved.",
  probableNote:
    "{n} is above 3.3 × 10²⁴, where no short fixed list of bases is proven. It passed Miller–Rabin for the first {count} prime bases, so it is almost certainly prime — but this test alone is not a proof. A primality certificate (ECPP) would be.",
  mrHeading: "Miller–Rabin working",
  mrIntro:
    "Write n − 1 = 2^s × d with d odd. For each base a, compute a^d mod n and square it up to s − 1 times. n passes the base if a^d ≡ 1, or if some square equals n − 1. Failing even one base proves n is composite.",
  mrDecomposition: "n − 1 = {m} = 2^{s} × {d}",
  mrBase: "Base a",
  mrSequence: "a^d mod n, then each squaring",
  mrResult: "Result",
  outcomeOne: "Starts at 1: passes",
  outcomeMinusOne: "Reaches n − 1: passes",
  outcomeWitness: "Never reaches n − 1: witness, n is composite",
  factorsHeading: "Factors",
  factorsLine: "{n} = {factors}",
  factorsTooLarge:
    "Finding the factors of a number this large is a much harder problem than testing it, so only the test is run.",
  found: "Smallest prime factor found by trial division: {p}.",
  neighboursHeading: "Neighbouring primes",
  neighboursGap: "The gap from the previous prime to the next is {gap}.",
  searchFailed: "No prime was found within the search limit.",
  enter: "Enter a whole number to test it.",
  invalid:
    "Enter a whole number, with no decimals, fractions or commas. A power such as 2^31-1 is fine.",
  negative:
    "Primes are positive whole numbers. Test the absolute value instead; negative numbers are not called prime in elementary number theory.",
  tooLarge: "Enter a number of at most {max} digits.",
  rangeHeading: "Primes in a range",
  rangeIntro:
    "A segmented sieve of Eratosthenes lists every prime between two numbers, up to {end}, for ranges of at most {width} numbers.",
  from: "From",
  to: "To",
  rangeCount: "Primes found",
  rangeFirst: "Smallest",
  rangeLast: "Largest",
  rangeGap: "Largest gap",
  rangeGapValue: "{gap}, between {from} and {to}",
  rangeTwins: "Twin prime pairs",
  rangeNone: "There are no primes in this range.",
  rangeList: "Primes",
  rangeTruncated:
    "Showing the first {first} and last {last} of {count} primes. The copy button copies them all.",
  copyList: "Copy list",
  rangeEmpty: "Enter both ends of the range to list its primes.",
  rangeInvalid: "Both ends must be whole numbers, with no decimals or commas.",
  rangeReversed: "The first number must not be larger than the second.",
  rangeTooLarge:
    "The sieve works up to {end}. Use the checker above for single numbers beyond that.",
  rangeTooWide: "Ask for at most {width} numbers at a time: narrow the range.",
} as const;

type Strings = typeof PRIME_NUMBER_CHECKER_STRINGS;

const LIST_LIMIT = 1000;
const LIST_HEAD = 500;
const LIST_TAIL = 100;
const SHORTEN_ABOVE = 28;
const SEQUENCE_SHOWN = 8;
const SEQUENCE_HEAD = 5;
const SEQUENCE_TAIL = 2;
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const LIST_BOX =
  "break-words rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";

/** A long residue shown as its first and last digits. */
function shorten(value: bigint): string {
  const text = value.toString();
  if (text.length <= SHORTEN_ABOVE) {
    return text;
  }
  return `${text.slice(0, 12)}…${text.slice(-12)} (${text.length})`;
}

/** A long squaring chain (s can be 100+) shown as its start and its end. */
function sequenceText(sequence: readonly bigint[]): string {
  if (sequence.length <= SEQUENCE_SHOWN) {
    return sequence.map(shorten).join(" → ");
  }
  const head = sequence.slice(0, SEQUENCE_HEAD).map(shorten);
  const tail = sequence.slice(-SEQUENCE_TAIL).map(shorten);
  return [...head, "…", ...tail].join(" → ");
}

function outcomeLabel(round: MillerRabinRound, strings: Strings): string {
  if (round.outcome === "one") {
    return strings.outcomeOne;
  }
  return round.outcome === "minus-one"
    ? strings.outcomeMinusOne
    : strings.outcomeWitness;
}

function verdictLabel(result: PrimalityResult, strings: Strings): string {
  switch (result.verdict) {
    case "prime":
      return strings.prime;
    case "probable-prime":
      return strings.probablePrime;
    case "composite":
      return strings.composite;
    default:
      return strings.neither;
  }
}

function summaryFor(result: PrimalityResult, n: bigint, strings: Strings) {
  const templates = {
    prime: strings.summaryPrime,
    "probable-prime": strings.summaryProbable,
    composite: strings.summaryComposite,
    neither: strings.summaryNeither,
  };
  return t(templates[result.verdict], { n: String(n) });
}

function MillerRabinTable({
  n,
  result,
  strings,
}: {
  n: bigint;
  result: PrimalityResult;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (result.decomposition === null) {
    return null;
  }
  const { s, d } = result.decomposition;
  const witness = result.rounds.find((r) => r.outcome === "witness");
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.mrHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.mrIntro}</p>
      <div className={MONO_LINES}>
        <p>
          {t(strings.mrDecomposition, {
            m: shorten(n - BigInt(1)),
            s: String(s),
            d: shorten(d),
          })}
        </p>
      </div>
      <DataTable
        head={[strings.mrBase, strings.mrSequence, strings.mrResult]}
        rows={result.rounds.map((round) => [
          String(round.base),
          sequenceText(round.sequence),
          outcomeLabel(round, strings),
        ])}
      />
      {result.verdict === "prime" ? (
        <Notice>
          {t(strings.provenNote, {
            count: fmt(result.rounds.length),
            bases: tPlural(strings.baseOne, locale, result.rounds.length),
            limit: String(provenLimit(result.rounds.length) ?? ""),
          })}
        </Notice>
      ) : null}
      {witness ? (
        <Notice>
          {t(strings.witnessNote, { base: String(witness.base), n: String(n) })}
        </Notice>
      ) : null}
      {result.verdict === "probable-prime" ? (
        <Notice>
          {t(strings.probableNote, {
            n: shorten(n),
            count: fmt(result.rounds.length),
          })}
        </Notice>
      ) : null}
    </>
  );
}

function Factors({
  n,
  result,
  strings,
}: {
  n: bigint;
  result: PrimalityResult;
  strings: Strings;
}) {
  const factored = useMemo(
    () => (n <= MAX_FACTOR_INPUT ? factorize(n) : null),
    [n]
  );
  if (factored?.complete) {
    return (
      <>
        <h3 className={WIDGET_H3}>{strings.factorsHeading}</h3>
        <div className={MONO_LINES}>
          <p>
            {t(strings.factorsLine, {
              n: String(n),
              factors: exponentForm(factored.factors),
            })}
          </p>
        </div>
      </>
    );
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.factorsHeading}</h3>
      <Notice>
        {result.smallFactor === null
          ? strings.factorsTooLarge
          : `${t(strings.found, { p: String(result.smallFactor) })} ${strings.factorsTooLarge}`}
      </Notice>
    </>
  );
}

function Neighbours({
  n,
  isPrime,
  strings,
}: {
  n: bigint;
  isPrime: boolean;
  strings: Strings;
}) {
  const { previous, next } = useMemo(
    () => ({ previous: previousPrime(n), next: nextPrime(n) }),
    [n]
  );
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.neighboursHeading}</h3>
      <StatGrid>
        <StatCard
          label={strings.previousPrime}
          value={previous === null ? strings.noPrevious : String(previous)}
        />
        <StatCard
          label={strings.nextPrime}
          value={next === null ? strings.searchFailed : String(next)}
        />
      </StatGrid>
      {isPrime && previous !== null && next !== null ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.neighboursGap, { gap: String(next - previous) })}
        </p>
      ) : null}
    </>
  );
}

function CheckResults({ n, strings }: { n: bigint; strings: Strings }) {
  const fmt = useToolNumber();
  const result = useMemo(() => testPrimality(n), [n]);
  const digits = n.toString().length;
  const summary = summaryFor(result, n, strings);
  const composite = result.verdict === "composite";
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.verdict}
          value={verdictLabel(result, strings)}
        />
        <StatCard label={strings.digits} value={fmt(digits)} />
        {result.smallFactor !== null ? (
          <StatCard
            label={strings.smallestFactor}
            value={String(result.smallFactor)}
          />
        ) : null}
      </StatGrid>
      <p className="text-foreground text-sm">{summary}</p>
      {result.verdict === "neither" ? (
        <Notice>{strings.explainNeither}</Notice>
      ) : null}
      {result.smallFactor !== null ? (
        <Notice>
          {t(strings.explainTrial, {
            p: String(result.smallFactor),
            n: String(n),
            q: String(n / result.smallFactor),
          })}
        </Notice>
      ) : null}
      {result.verdict === "prime" && result.rounds.length === 0 ? (
        <Notice>{t(strings.explainSmallPrime, { n: String(n) })}</Notice>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <MillerRabinTable n={n} result={result} strings={strings} />
      {composite ? <Factors n={n} result={result} strings={strings} /> : null}
      <Neighbours
        isPrime={
          result.verdict === "prime" || result.verdict === "probable-prime"
        }
        n={n}
        strings={strings}
      />
    </>
  );
}

function CheckOutcome({
  parsed,
  strings,
}: {
  parsed: ParsedNumber;
  strings: Strings;
}) {
  switch (parsed.kind) {
    case "ok":
      return <CheckResults n={parsed.value} strings={strings} />;
    case "empty":
      return <Notice>{strings.enter}</Notice>;
    case "negative":
      return <ErrorNotice>{strings.negative}</ErrorNotice>;
    case "too-large":
      return (
        <ErrorNotice>{t(strings.tooLarge, { max: MAX_DIGITS })}</ErrorNotice>
      );
    default:
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
}

function RangeResults({
  lo,
  hi,
  strings,
}: {
  lo: number;
  hi: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const primes = useMemo(() => primesInRange(lo, hi), [lo, hi]);
  const stats = useMemo(() => rangeStats(primes), [primes]);
  if (primes.length === 0) {
    return <Notice>{strings.rangeNone}</Notice>;
  }
  const truncated = primes.length > LIST_LIMIT;
  const shown = truncated
    ? `${primes.slice(0, LIST_HEAD).join(", ")} … ${primes.slice(-LIST_TAIL).join(", ")}`
    : primes.join(", ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.rangeCount}
          value={fmt(stats.count)}
        />
        <StatCard label={strings.rangeFirst} value={String(stats.first)} />
        <StatCard label={strings.rangeLast} value={String(stats.last)} />
        <StatCard label={strings.rangeTwins} value={fmt(stats.twinPairs)} />
        {stats.largestGap ? (
          <StatCard
            label={strings.rangeGap}
            value={t(strings.rangeGapValue, {
              gap: fmt(stats.largestGap.gap),
              from: String(stats.largestGap.from),
              to: String(stats.largestGap.to),
            })}
          />
        ) : null}
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.rangeList}</h3>
      <p className={LIST_BOX}>{shown}</p>
      {truncated ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.rangeTruncated, {
            first: fmt(LIST_HEAD),
            last: fmt(LIST_TAIL),
            count: fmt(primes.length),
          })}
        </p>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyList}
        small
        text={primes.join(", ")}
      />
    </>
  );
}

function RangeOutcome({
  rawLo,
  rawHi,
  strings,
}: {
  rawLo: string;
  rawHi: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const read = useMemo(() => readRange(rawLo, rawHi), [rawLo, rawHi]);
  switch (read.kind) {
    case "ok":
      return <RangeResults hi={read.hi} lo={read.lo} strings={strings} />;
    case "empty":
      return <Notice>{strings.rangeEmpty}</Notice>;
    case "reversed":
      return <ErrorNotice>{strings.rangeReversed}</ErrorNotice>;
    case "too-large":
      return (
        <ErrorNotice>
          {t(strings.rangeTooLarge, { end: fmt(MAX_RANGE_END) })}
        </ErrorNotice>
      );
    case "too-wide":
      return (
        <ErrorNotice>
          {t(strings.rangeTooWide, { width: fmt(MAX_RANGE_WIDTH) })}
        </ErrorNotice>
      );
    default:
      return <ErrorNotice>{strings.rangeInvalid}</ErrorNotice>;
  }
}

export default function PrimeNumberCheckerWidget() {
  const strings = useToolUi(PRIME_NUMBER_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("1000003");
  const [rawLo, setRawLo] = useState("1");
  const [rawHi, setRawHi] = useState("100");
  const deferred = useDeferredValue(raw);
  const deferredLo = useDeferredValue(rawLo);
  const deferredHi = useDeferredValue(rawHi);
  const parsed = useMemo(() => parseCandidate(deferred), [deferred]);
  return (
    <>
      <TextField
        hint={strings.numberHint}
        id="pnc-number"
        label={strings.number}
        mono
        numeric
        onChange={setRaw}
        value={raw}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <CheckOutcome parsed={parsed} strings={strings} />
      </section>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.rangeHeading}</h2>
        <p className="text-muted-foreground text-sm">
          {t(strings.rangeIntro, {
            end: fmt(MAX_RANGE_END),
            width: fmt(MAX_RANGE_WIDTH),
          })}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pnc-from"
            label={strings.from}
            mono
            numeric
            onChange={setRawLo}
            value={rawLo}
          />
          <TextField
            id="pnc-to"
            label={strings.to}
            mono
            numeric
            onChange={setRawHi}
            value={rawHi}
          />
        </div>
        <div aria-live="polite" className="space-y-4">
          <RangeOutcome
            rawHi={deferredHi}
            rawLo={deferredLo}
            strings={strings}
          />
        </div>
      </section>
    </>
  );
}
